/**
 * 天地图 JS API 4.0 → 现有 BMapGL 调用面适配。
 * 业务侧仍传 BD09；进出天地图时转换为 CGCS2000/WGS84。
 */
import { bd09ToWgs84, wgs84ToBd09 } from './coordBd09Wgs';

const TMAP_SAT = 'TMAP_SATELLITE';
const TMAP_HYBRID = 'TMAP_HYBRID';
const TMAP_NORMAL = 'TMAP_NORMAL';

function T() {
  return window.T;
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function coordNum(obj, axis) {
  if (!obj) return null;
  const getterName = axis === 'lng' ? 'getLng' : axis === 'lat' ? 'getLat' : `get${axis.charAt(0).toUpperCase()}${axis.slice(1)}`;
  const getter = obj[getterName];
  if (typeof getter === 'function') {
    const n = toNum(getter.call(obj));
    if (n != null) return n;
  }
  const v = obj[axis];
  if (typeof v === 'function') return toNum(v.call(obj));
  return toNum(v);
}

function readLngLat(p) {
  if (!p) return null;
  if (Array.isArray(p) && p.length >= 2) {
    const lng = toNum(p[0]);
    const lat = toNum(p[1]);
    if (lng == null || lat == null) return null;
    return { lng, lat };
  }
  const lng = toNum(p.lng ?? p.lon ?? p.longitude);
  const lat = toNum(p.lat ?? p.latitude);
  if (lng == null || lat == null) return null;
  return { lng, lat };
}

function toTdtLngLat(p) {
  const ll = readLngLat(p);
  if (!ll) return null;
  const wgs = bd09ToWgs84(ll.lng, ll.lat);
  return new T().LngLat(wgs.lng, wgs.lat);
}

function fromTdtLngLat(lnglat) {
  if (!lnglat) return null;
  const lng = coordNum(lnglat, 'lng');
  const lat = coordNum(lnglat, 'lat');
  if (lng == null || lat == null) return null;
  const bd = wgs84ToBd09(lng, lat);
  return new Point(bd.lng, bd.lat);
}

function rasterizeSvgIconUrl(url, w, h) {
  if (!url || typeof url !== 'string' || !url.includes('image/svg')) return url;
  try {
    const raw = url.replace(/^data:image\/svg\+xml[^,]*,/, '');
    const decoded = decodeURIComponent(raw);
    const fill = decoded.match(/fill="([^"]+)"/)?.[1] || '#1677ff';
    const stroke = decoded.match(/stroke="([^"]+)"/)?.[1] || '#ffffff';
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(8, Math.round(w) || 24);
    canvas.height = Math.max(8, Math.round(h) || 24);
    const ctx = canvas.getContext('2d');
    if (!ctx) return url;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const r = Math.max(2, Math.min(cx, cy) - 2);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = stroke;
    ctx.stroke();
    return canvas.toDataURL('image/png');
  } catch (_) {
    return url;
  }
}

function toTStyle(opts = {}) {
  if (!opts) return {};
  return {
    color: opts.strokeColor || opts.color || '#3388ff',
    weight: opts.strokeWeight ?? opts.weight ?? 2,
    opacity: opts.strokeOpacity ?? opts.opacity ?? 1,
    fillColor: opts.fillColor || opts.strokeColor || '#3388ff',
    fillOpacity: opts.fillOpacity ?? 0.3,
    lineStyle: opts.strokeStyle || opts.lineStyle || 'solid'
  };
}

function applyLabelStyle(label, style) {
  if (!label || !style || typeof style !== 'object') return;
  try {
    if (style.color) label.setFontColor?.(style.color);
    if (style.fontSize) {
      const n = parseInt(String(style.fontSize), 10);
      if (Number.isFinite(n)) label.setFontSize?.(n);
    }
    if (style.backgroundColor) label.setBackgroundColor?.(style.backgroundColor);
    if (style.borderColor) label.setBorderColor?.(style.borderColor);
    if (style.border === 'none' || style.border === 0 || style.border === '0') {
      label.setBorderLine?.(0);
    } else if (style.border) {
      label.setBorderLine?.(1);
    }
  } catch (_) {
    /* ignore */
  }
  try {
    const el = typeof label.getElement === 'function' ? label.getElement() : label._element;
    if (el?.style) {
      Object.keys(style).forEach((k) => {
        try {
          el.style[k] = style[k];
        } catch (_) {
          /* ignore */
        }
      });
    }
  } catch (_) {
    /* ignore */
  }
}

function wrapOverlayEvent(e) {
  const lnglat = e?.lnglat || e?.lngLat || e?.latlng || e?.latLng || e?.lonlat
    || (typeof e?.target?.getLngLat === 'function' ? e.target.getLngLat() : null);
  const point = fromTdtLngLat(lnglat);
  const cx = coordNum(e?.containerPoint, 'x');
  const cy = coordNum(e?.containerPoint, 'y');
  const dom = e?.originalEvent || e?.domEvent || e?.srcEvent || null;
  return {
    ...e,
    point,
    latLng: point,
    lnglat: point,
    pixel: cx != null && cy != null ? new Pixel(cx, cy) : e?.pixel,
    overlay: e?.target?._bmapWrapper || e?.overlay,
    target: e?.target?._bmapWrapper || e?.target,
    domEvent: dom,
    originalEvent: dom
  };
}

function bindEvents(native, wrapper) {
  if (!native || typeof native.addEventListener !== 'function') return;
  native._bmapWrapper = wrapper;
  wrapper.addEventListener = (type, fn) => {
    if (!type || typeof fn !== 'function') return;
    const handler = (e) => fn(wrapOverlayEvent(e || {}));
    // 天地图 Marker 拖动主事件是 drag；业务侧习惯 dragging
    const aliases = type === 'dragging' || type === 'drag' ? ['dragging', 'drag'] : [type];
    aliases.forEach((t) => {
      try {
        native.addEventListener(t, handler);
      } catch (_) {
        /* ignore */
      }
    });
    if (!wrapper._handlers) wrapper._handlers = [];
    wrapper._handlers.push({ type, aliases, fn, handler });
  };
  wrapper.removeEventListener = (type, fn) => {
    if (!wrapper._handlers) return;
    wrapper._handlers = wrapper._handlers.filter((h) => {
      if (h.type === type && (!fn || h.fn === fn)) {
        (h.aliases || [type]).forEach((t) => {
          try {
            native.removeEventListener(t, h.handler);
          } catch (_) {
            /* ignore */
          }
        });
        return false;
      }
      return true;
    });
  };
}

class Point {
  constructor(lng, lat) {
    this.lng = Number(lng);
    this.lat = Number(lat);
  }

  equals(other) {
    return other && this.lng === other.lng && this.lat === other.lat;
  }
}

class Size {
  constructor(width, height) {
    this.width = Number(width) || 0;
    this.height = Number(height) || 0;
    this.x = this.width;
    this.y = this.height;
  }
}

class Pixel {
  constructor(x, y) {
    this.x = Number(x) || 0;
    this.y = Number(y) || 0;
  }
}

class Icon {
  constructor(url, size, opts = {}) {
    this.imageUrl = url;
    this.size = size || new Size(24, 24);
    this.anchor = opts.anchor || new Size(this.size.width / 2, this.size.height / 2);
    this.imageSize = opts.imageSize || this.size;
    const w = this.size.width || 24;
    const h = this.size.height || 24;
    const iconUrl = rasterizeSvgIconUrl(url, w, h);
    const Tt = T();
    this._tdt = Tt?.Icon
      ? new Tt.Icon({
          iconUrl,
          iconSize: new Tt.Point(w, h),
          iconAnchor: new Tt.Point(this.anchor.width ?? this.anchor.x ?? w / 2, this.anchor.height ?? this.anchor.y ?? h / 2)
        })
      : null;
  }
}

class Label {
  constructor(content, opts = {}) {
    this._content = content == null ? '' : String(content);
    this._offset = opts.offset || new Size(0, 0);
    this._position = opts.position || null;
    const Tt = T();
    const pos = this._position ? toTdtLngLat(this._position) : new Tt.LngLat(0, 0);
    this._tdt = new Tt.Label({
      text: this._content,
      position: pos,
      offset: new Tt.Point(this._offset.width ?? this._offset.x ?? 0, this._offset.height ?? this._offset.y ?? 0)
    });
    bindEvents(this._tdt, this);
  }

  setContent(text) {
    this._content = text == null ? '' : String(text);
    try {
      this._tdt.setLabel?.(this._content);
      this._tdt.setContent?.(this._content);
      this._tdt.setText?.(this._content);
    } catch (_) {
      /* ignore */
    }
  }

  setPosition(point) {
    this._position = point;
    const ll = toTdtLngLat(point);
    if (ll) {
      try {
        this._tdt.setLngLat(ll);
      } catch (_) {
        /* ignore */
      }
    }
  }

  setStyle(style) {
    applyLabelStyle(this._tdt, style);
  }

  setOffset(size) {
    this._offset = size;
  }
}

class Marker {
  constructor(point, opts = {}) {
    this._bdPoint = new Point(point.lng, point.lat);
    const Tt = T();
    const nativeOpts = {};
    if (opts.icon?._tdt) nativeOpts.icon = opts.icon._tdt;
    this._wantDrag = !!(opts.enableDragging || opts.draggable);
    if (this._wantDrag) nativeOpts.draggable = true;
    this._tdt = new Tt.Marker(toTdtLngLat(point), nativeOpts);
    bindEvents(this._tdt, this);
    this._label = null;
    this._map = null;
    this._dragGuardBound = false;
    if (this._wantDrag) {
      this._ensureDragGuard();
    }
  }

  setPosition(point) {
    this._bdPoint = new Point(point.lng, point.lat);
    const ll = toTdtLngLat(point);
    if (ll) this._tdt.setLngLat(ll);
  }

  getPosition() {
    const live = fromTdtLngLat(this._tdt.getLngLat?.());
    return live || this._bdPoint;
  }

  setIcon(icon) {
    if (icon?._tdt) {
      try {
        this._tdt.setIcon(icon._tdt);
      } catch (_) {
        /* ignore */
      }
    }
  }

  setLabel(label) {
    this._label = label;
    if (!label?._tdt) return;
    try {
      if (typeof this._tdt.setLabel === 'function') {
        this._tdt.setLabel(label._tdt);
      }
    } catch (_) {
      /* ignore */
    }
  }

  setTitle(title) {
    this._title = title;
    try {
      this._tdt.setTitle?.(title || '');
    } catch (_) {
      /* ignore */
    }
  }

  /** 拖顶点时暂时关掉底图拖拽，松手后恢复 */
  _ensureDragGuard() {
    if (this._dragGuardBound || !this._tdt) return;
    this._dragGuardBound = true;
    const pauseMap = () => {
      try {
        this._map?.disableDragging?.();
      } catch (_) {
        /* ignore */
      }
    };
    const resumeMap = () => {
      try {
        this._map?.enableDragging?.();
      } catch (_) {
        /* ignore */
      }
    };
    try {
      this._tdt.addEventListener('dragstart', pauseMap);
      this._tdt.addEventListener('dragend', resumeMap);
    } catch (_) {
      /* ignore */
    }
  }

  enableDragging() {
    this._wantDrag = true;
    this._ensureDragGuard();
    try {
      this._tdt.enableDragging?.();
    } catch (_) {
      /* ignore */
    }
    try {
      this._tdt.setDraggable?.(true);
    } catch (_) {
      /* ignore */
    }
    try {
      if (this._tdt.options) this._tdt.options.draggable = true;
    } catch (_) {
      /* ignore */
    }
  }

  disableDragging() {
    this._wantDrag = false;
    try {
      this._tdt.disableDragging?.();
    } catch (_) {
      /* ignore */
    }
    try {
      this._tdt.setDraggable?.(false);
    } catch (_) {
      /* ignore */
    }
    try {
      if (this._tdt.options) this._tdt.options.draggable = false;
    } catch (_) {
      /* ignore */
    }
  }
}

class Polygon {
  constructor(path, opts = {}) {
    this._bdPath = (Array.isArray(path) ? path : []).map((p) => new Point(p.lng, p.lat));
    const Tt = T();
    const lnglats = this._bdPath.map((p) => toTdtLngLat(p)).filter(Boolean);
    this._tdt = new Tt.Polygon(lnglats, toTStyle(opts));
    bindEvents(this._tdt, this);
  }

  getPath() {
    return this._bdPath.map((p) => new Point(p.lng, p.lat));
  }

  setPath(path) {
    this._bdPath = (Array.isArray(path) ? path : []).map((p) => new Point(p.lng, p.lat));
    const lnglats = this._bdPath.map((p) => toTdtLngLat(p)).filter(Boolean);
    try {
      this._tdt.setLngLats(lnglats);
    } catch (_) {
      /* ignore */
    }
  }

  setFillColor(c) {
    try {
      this._tdt.setFillColor(c);
    } catch (_) {
      /* ignore */
    }
  }

  setStrokeColor(c) {
    try {
      this._tdt.setColor(c);
    } catch (_) {
      /* ignore */
    }
  }

  setStrokeWeight(w) {
    try {
      this._tdt.setWeight(w);
    } catch (_) {
      /* ignore */
    }
  }

  setFillOpacity(v) {
    try {
      this._tdt.setFillOpacity(v);
    } catch (_) {
      /* ignore */
    }
  }

  setStrokeOpacity(v) {
    try {
      this._tdt.setOpacity(v);
    } catch (_) {
      /* ignore */
    }
  }

  enableEditing() {}

  disableEditing() {}
}

class Polyline {
  constructor(path, opts = {}) {
    this._bdPath = (Array.isArray(path) ? path : []).map((p) => new Point(p.lng, p.lat));
    const Tt = T();
    const lnglats = this._bdPath.map((p) => toTdtLngLat(p)).filter(Boolean);
    const style = toTStyle(opts);
    delete style.fillColor;
    delete style.fillOpacity;
    this._tdt = new Tt.Polyline(lnglats, style);
    bindEvents(this._tdt, this);
  }

  getPath() {
    return this._bdPath.map((p) => new Point(p.lng, p.lat));
  }

  setPath(path) {
    this._bdPath = (Array.isArray(path) ? path : []).map((p) => new Point(p.lng, p.lat));
    const lnglats = this._bdPath.map((p) => toTdtLngLat(p)).filter(Boolean);
    try {
      this._tdt.setLngLats(lnglats);
    } catch (_) {
      /* ignore */
    }
  }

  setStrokeColor(c) {
    try {
      this._tdt.setColor(c);
    } catch (_) {
      /* ignore */
    }
  }

  setStrokeWeight(w) {
    try {
      this._tdt.setWeight(w);
    } catch (_) {
      /* ignore */
    }
  }
}

class Circle {
  constructor(center, radius, opts = {}) {
    this._bdCenter = new Point(center.lng, center.lat);
    this._radius = Number(radius) || 0;
    const Tt = T();
    this._tdt = new Tt.Circle(toTdtLngLat(center), this._radius, toTStyle(opts));
    bindEvents(this._tdt, this);
  }

  setCenter(point) {
    this._bdCenter = new Point(point.lng, point.lat);
    try {
      this._tdt.setCenter(toTdtLngLat(point));
    } catch (_) {
      /* ignore */
    }
  }

  setRadius(r) {
    this._radius = Number(r) || 0;
    try {
      this._tdt.setRadius(this._radius);
    } catch (_) {
      /* ignore */
    }
  }

  setTitle(title) {
    this._title = title;
  }
}

class InfoWindow {
  constructor(content, opts = {}) {
    this._content = content;
    this._opts = opts || {};
    const Tt = T();
    const offset = opts.offset;
    this._tdt = new Tt.InfoWindow(content, {
      autoPan: true,
      offset: offset ? new Tt.Point(offset.width ?? offset.x ?? 0, offset.height ?? offset.y ?? 0) : new Tt.Point(0, -24)
    });
  }
}

class Geolocation {
  getCurrentPosition(callback, options) {
    const Tt = T();
    const done = (status, result) => {
      try {
        callback?.call({ getStatus: () => status }, result);
      } catch (_) {
        /* ignore */
      }
    };
    if (typeof callback !== 'function' || !Tt?.Geolocation) {
      done(6, null);
      return;
    }
    try {
      const geo = new Tt.Geolocation();
      const onPos = function onPos(e) {
        const rawStatus = Number(typeof this?.getStatus === 'function' ? this.getStatus() : e?.status);
        const point = fromTdtLngLat(e?.lnglat || e?.lngLat);
        const ok = Boolean(point) && (rawStatus === 0 || rawStatus === 1);
        if (ok) {
          done(0, {
            point,
            address: e?.address || {},
            accuracy: e?.accuracy
          });
          return;
        }
        done(rawStatus === 1 || rawStatus === 6 ? 6 : 2, null);
      };
      try {
        geo.getCurrentPosition(onPos, options);
      } catch (_) {
        geo.getCurrentPosition(onPos);
      }
    } catch (_) {
      done(2, null);
    }
  }
}

class Geocoder {
  getLocation(point, callback) {
    const Tt = T();
    const done = (rs) => {
      try {
        callback?.(rs);
      } catch (_) {
        /* ignore */
      }
    };
    if (!Tt?.Geocoder) {
      done(null);
      return;
    }
    try {
      const geoc = new Tt.Geocoder();
      geoc.getLocation(toTdtLngLat(point), (result) => {
        if (!result) {
          done(null);
          return;
        }
        const addr = typeof result.getAddress === 'function' ? result.getAddress() : result.formatted_address;
        const compRaw = typeof result.getAddressComponent === 'function'
          ? result.getAddressComponent()
          : (result.addressComponent || {});
        const comp = {
          province: compRaw.province || '',
          city: compRaw.city || '',
          district: compRaw.district || compRaw.county || '',
          street: compRaw.street || compRaw.road || '',
          streetNumber: compRaw.streetNumber || compRaw.poi_position || ''
        };
        done({
          address: addr || `${comp.province}${comp.city}${comp.district}${comp.street}${comp.streetNumber}`,
          addressComponents: comp
        });
      });
    } catch (_) {
      done(null);
    }
  }
}

class Map {
  constructor(container) {
    const el = typeof container === 'string' ? document.getElementById(container) : container;
    if (!el) throw new Error('地图容器不存在');
    if (!el.id) el.id = `tdt_map_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    this._el = el;
    const Tt = T();
    this._tdt = new Tt.Map(el, { minZoom: 3, maxZoom: 18 });
    this._overlays = [];
    this._listeners = [];
    try {
      this._tdt.centerAndZoom(new Tt.LngLat(108.94, 34.27), 12);
    } catch (_) {
      /* ignore */
    }
    this.enableScrollWheelZoom(true);
    this.enableDragging();
    this.setMapType(window.BMAP_SATELLITE_MAP);
    try {
      // 关掉惯性拖拽，避免松手后地图还在滑、误触更严重
      this._tdt.disableInertia?.();
      this._tdt.setInertia?.(false);
    } catch (_) {
      /* ignore */
    }
    try {
      this._tdt.checkResize?.();
    } catch (_) {
      /* ignore */
    }
  }

  _nativeType(code) {
    if (code === TMAP_NORMAL) return window.TMAP_NORMAL_MAP;
    if (code === TMAP_SAT) return window.TMAP_SATELLITE_MAP || window.TMAP_HYBRID_MAP;
    return window.TMAP_HYBRID_MAP || window.TMAP_SATELLITE_MAP || window.TMAP_NORMAL_MAP;
  }

  setMapType(type) {
    const native = this._nativeType(type) || type;
    if (!native) return;
    if (this._mapType === native) return;
    this._mapType = native;
    try {
      this._tdt.setMapType(native);
    } catch (_) {
      /* ignore */
    }
  }

  enableScrollWheelZoom() {
    try {
      this._tdt.enableScrollWheelZoom();
    } catch (_) {
      /* ignore */
    }
  }

  disableScrollWheelZoom() {
    try {
      this._tdt.disableScrollWheelZoom();
    } catch (_) {
      /* ignore */
    }
  }

  enableDragging() {
    try {
      this._tdt.enableDrag?.();
    } catch (_) {
      /* ignore */
    }
    try {
      this._tdt.enableDragging?.();
    } catch (_) {
      /* ignore */
    }
  }

  disableDragging() {
    try {
      this._tdt.disableDrag?.();
    } catch (_) {
      /* ignore */
    }
    try {
      this._tdt.disableDragging?.();
    } catch (_) {
      /* ignore */
    }
  }

  enableDoubleClickZoom() {
    try {
      this._tdt.enableDoubleClickZoom();
    } catch (_) {
      /* ignore */
    }
  }

  disableDoubleClickZoom() {
    try {
      this._tdt.disableDoubleClickZoom();
    } catch (_) {
      /* ignore */
    }
  }

  setMinZoom(z) {
    try {
      this._tdt.setMinZoom(z);
    } catch (_) {
      /* ignore */
    }
  }

  setMaxZoom(z) {
    try {
      this._tdt.setMaxZoom(Math.min(Number(z) || 18, 18));
    } catch (_) {
      /* ignore */
    }
  }

  getZoom() {
    try {
      return this._tdt.getZoom();
    } catch (_) {
      return 12;
    }
  }

  setZoom(z) {
    try {
      const next = Number(z);
      if (!Number.isFinite(next)) return;
      const cur = this.getZoom();
      // 同级再 setZoom 会反复触发 zoomend，天地图下会卡死
      if (Number.isFinite(cur) && Math.abs(cur - next) < 0.05) return;
      this._tdt.setZoom(next);
    } catch (_) {
      /* ignore */
    }
  }

  getCenter() {
    try {
      return fromTdtLngLat(this._tdt.getCenter());
    } catch (_) {
      return new Point(108.94, 34.27);
    }
  }

  centerAndZoom(point, zoom) {
    const ll = toTdtLngLat(point);
    if (!ll) return;
    try {
      this._tdt.centerAndZoom(ll, zoom);
    } catch (_) {
      /* ignore */
    }
  }

  panTo(point) {
    const ll = toTdtLngLat(point);
    if (!ll) return;
    try {
      this._tdt.panTo(ll);
    } catch (_) {
      /* ignore */
    }
  }

  setViewport(points) {
    const arr = (Array.isArray(points) ? points : []).map((p) => toTdtLngLat(p)).filter(Boolean);
    if (arr.length === 0) return;
    try {
      this._tdt.setViewport(arr);
    } catch (_) {
      if (arr.length === 1) this.centerAndZoom(points[0], 16);
    }
  }

  addOverlay(overlay) {
    if (!overlay) return;
    this._overlays.push(overlay);
    try {
      overlay._map = this;
    } catch (_) {
      /* ignore */
    }
    const native = overlay._tdt || overlay;
    try {
      this._tdt.addOverLay(native);
    } catch (_) {
      /* ignore */
    }
    // 天地图要求：先入图再 enableDragging，否则拖顶点会变成拖地图
    if (typeof overlay.enableDragging === 'function' && overlay._wantDrag) {
      try {
        overlay.enableDragging();
      } catch (_) {
        /* ignore */
      }
    }
    const boundLabel = overlay._label;
    if (boundLabel?._tdt) {
      try {
        const pos = typeof overlay.getPosition === 'function' ? overlay.getPosition() : overlay._bdPoint;
        if (pos) boundLabel.setPosition(pos);
        this._tdt.addOverLay(boundLabel._tdt);
        this._overlays.push(boundLabel);
      } catch (_) {
        /* ignore */
      }
    }
  }

  removeOverlay(overlay) {
    if (!overlay) return;
    this._overlays = this._overlays.filter((o) => o !== overlay);
    const native = overlay._tdt || overlay;
    try {
      this._tdt.removeOverLay(native);
    } catch (_) {
      /* ignore */
    }
  }

  clearOverlays() {
    this._overlays = [];
    try {
      this._tdt.clearOverLays();
    } catch (_) {
      /* ignore */
    }
  }

  addEventListener(type, fn) {
    if (!type || typeof fn !== 'function') return;
    const handler = (e) => {
      const wrapped = wrapOverlayEvent(e || {});
      if (!wrapped.point && e?.lnglat) {
        wrapped.point = fromTdtLngLat(e.lnglat);
        wrapped.latLng = wrapped.point;
        wrapped.lnglat = wrapped.point;
      }
      // 点击落在面/线上时，天地图有时不给 lnglat：用容器像素反算
      if (!wrapped.point) {
        try {
          const de = wrapped.domEvent || e?.originalEvent || e;
          const t = de?.changedTouches?.[0] || de?.touches?.[0] || de;
          if (t && (t.clientX != null || t.pageX != null) && this._el) {
            const rect = this._el.getBoundingClientRect();
            const x = Number(t.clientX ?? t.pageX) - rect.left;
            const y = Number(t.clientY ?? t.pageY) - rect.top;
            if (Number.isFinite(x) && Number.isFinite(y)) {
              const pt = this.pixelToPoint(new Pixel(x, y));
              if (pt && Number.isFinite(pt.lng) && Number.isFinite(pt.lat)) {
                wrapped.point = pt;
                wrapped.latLng = pt;
                wrapped.lnglat = pt;
                wrapped.pixel = new Pixel(x, y);
              }
            }
          }
        } catch (_) {
          /* ignore */
        }
      }
      if (e?.containerPoint) {
        wrapped.pixel = new Pixel(e.containerPoint.x, e.containerPoint.y);
      }
      wrapped.domEvent = wrapped.domEvent || e?.originalEvent || e?.domEvent || e;
      fn(wrapped);
    };
    this._tdt.addEventListener(type, handler);
    this._listeners.push({ type, fn, handler });
  }

  removeEventListener(type, fn) {
    this._listeners = this._listeners.filter((h) => {
      if (h.type === type && (!fn || h.fn === fn)) {
        try {
          this._tdt.removeEventListener(h.type, h.handler);
        } catch (_) {
          /* ignore */
        }
        return false;
      }
      return true;
    });
  }

  pixelToPoint(pixel) {
    const x = coordNum(pixel, 'x');
    const y = coordNum(pixel, 'y');
    if (x == null || y == null) return null;
    const Tt = T();
    const pt = new Tt.Point(x, y);
    const tryFns = [
      () => this._tdt.containerPointToLngLat?.(pt),
      () => this._tdt.layerPointToLngLat?.(pt),
      () => this._tdt.overlayPixelToLngLat?.(pt)
    ];
    for (const fn of tryFns) {
      try {
        const ll = fn();
        const converted = fromTdtLngLat(ll);
        if (converted && Number.isFinite(converted.lng) && Number.isFinite(converted.lat)) {
          return converted;
        }
      } catch (_) {
        /* ignore */
      }
    }
    return null;
  }

  pointToPixel(point) {
    const ll = toTdtLngLat(point);
    const tryFns = [
      () => this._tdt.lngLatToContainerPoint?.(ll),
      () => this._tdt.lngLatToLayerPoint?.(ll)
    ];
    for (const fn of tryFns) {
      try {
        const p = fn();
        const x = coordNum(p, 'x');
        const y = coordNum(p, 'y');
        if (x != null && y != null) return new Pixel(x, y);
      } catch (_) {
        /* ignore */
      }
    }
    return new Pixel(0, 0);
  }

  getContainer() {
    return this._el;
  }

  checkResize() {
    try {
      this._tdt.checkResize?.();
    } catch (_) {
      /* ignore */
    }
  }

  resize() {
    this.checkResize();
  }

  setDefaultCursor(cursor) {
    try {
      this._tdt.setMapCursor(cursor);
    } catch (_) {
      if (this._el) this._el.style.cursor = cursor || '';
    }
  }

  setDraggingCursor(cursor) {
    this._dragCursor = cursor;
  }

  getDraggingCursor() {
    return this._dragCursor || '';
  }

  openInfoWindow(info, point) {
    if (!info?._tdt) return;
    try {
      this._tdt.openInfoWindow(info._tdt, toTdtLngLat(point));
    } catch (_) {
      /* ignore */
    }
  }

  closeInfoWindow() {
    try {
      this._tdt.closeInfoWindow();
    } catch (_) {
      /* ignore */
    }
  }

  destroy() {
    try {
      this._listeners.forEach((h) => this._tdt.removeEventListener(h.type, h.handler));
    } catch (_) {
      /* ignore */
    }
    this._listeners = [];
    try {
      this._tdt.clearOverLays?.();
    } catch (_) {
      /* ignore */
    }
    try {
      this._el.innerHTML = '';
    } catch (_) {
      /* ignore */
    }
    this._tdt = null;
    this._overlays = [];
  }
}

export function installTiandituBMapAdapter() {
  if (!window.T?.Map) return false;
  if (window.BMapGL?.__tiandituAdapter) return true;

  window.BMAP_SATELLITE_MAP = TMAP_HYBRID;
  window.BMAP_HYBRID_MAP = TMAP_HYBRID;
  window.BMAP_NORMAL_MAP = TMAP_NORMAL;

  window.BMapGL = {
    __tiandituAdapter: true,
    Map,
    Point,
    Size,
    Pixel,
    Icon,
    Label,
    Marker,
    Polygon,
    Polyline,
    Circle,
    InfoWindow,
    Geolocation,
    Geocoder,
    Projection: {
      convertMC2LL(pt) {
        if (!pt) return pt;
        const lng = toNum(pt.lng);
        const lat = toNum(pt.lat);
        if (lng == null || lat == null) return pt;
        if (Math.abs(lng) > 180 || Math.abs(lat) > 90) return null;
        return new Point(lng, lat);
      }
    }
  };
  return true;
}
